/**
 * Dashboard quality sections schema (#1338 PR B, program #1130 E2)
 *
 * Zod contract for GET /api/v1/dashboard/quality/sections — the «Качество»
 * tab's blocks whose data lives only in Postgres (script, objections,
 * compliance, AI accuracy, company criteria, review candidates). One adapter
 * over the released readers with the slice's window, scope and demo setting
 * (`dashboard-quality.ts`): half-open `[start, end)`, no previous window — so
 * no section carries a change (owner decision 09.10).
 *
 * Every section answers for itself: one source failing is that section's
 * `error`, never its neighbours', and never a zero.
 */

import { z } from 'zod/v4';
import {
  dashboardQualityQuerySchema,
  dashboardQualityScopeSchema,
  dashboardSliceWindowSchema,
} from './dashboard-quality';
import { dashboardAiAccuracySchema, dashboardComplianceCheckSchema } from './dashboard';
import { criteriaReportCriterionSchema } from './criteria-report';

// ============================================================================
// Query
// ============================================================================

/** The quality slice's query: the same window, zone and demo setting (`projectIds` read with `getAll`). */
export const dashboardQualitySectionsQuerySchema = dashboardQualityQuerySchema;

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();
const shareSchema = z.number().min(0).max(1);

/** Skipped steps a card lists; the rest is «ещё K» (`moreSkippedSteps`). */
export const QUALITY_SECTIONS_MAX_STEPS = 5;
/** Objection codes a card lists, most frequent first. */
export const QUALITY_SECTIONS_MAX_OBJECTIONS = 5;

/**
 * A section's state: `ok` — its data; `error` — its own source failed;
 * `noAccess` — the viewer lacks the section's own capability; `featureOff` —
 * the feature behind it is off for the organization.
 */
function section<T extends z.ZodType>(data: T) {
  return z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), data }),
    z.object({ status: z.literal('error') }),
    z.object({ status: z.literal('noAccess') }),
    z.object({ status: z.literal('featureOff') }),
  ]);
}

export const qualitySectionsScriptSchema = z.object({
  /** Mean adherence over the scored calls, the scale of `/agents/script-adherence`; `null` — none scored. */
  averageAdherence: z.number().nullable(),
  /** Calls scored against a script — the denominator. */
  scoredCalls: countSchema,
  /** Skipped steps by `compareSteps` (most calls that missed it first), at most `QUALITY_SECTIONS_MAX_STEPS`. */
  skippedSteps: z
    .array(
      z.object({
        stepId: z.string(),
        stepName: z.string(),
        /** Calls where the step was required. */
        owed: countSchema,
        notCompleted: countSchema,
        skipRate: shareSchema,
      })
    )
    .max(QUALITY_SECTIONS_MAX_STEPS),
  /** Skipped steps beyond the list — never cut silently. */
  moreSkippedSteps: countSchema,
});

export const qualitySectionsObjectionsSchema = z.object({
  /** Most frequent first, at most `QUALITY_SECTIONS_MAX_OBJECTIONS`. Codes and counts only — no client words. */
  codes: z
    .array(
      z.object({
        code: z.string(),
        /** Calls with the objection. */
        calls: countSchema,
        /** Of them, not handled. */
        unhandledCalls: countSchema,
      })
    )
    .max(QUALITY_SECTIONS_MAX_OBJECTIONS),
  /** Calls with any objection in the window — the denominator. */
  scannedCalls: countSchema,
  /** More calls than one scan reads: the counts are a lower bound. */
  truncated: z.boolean(),
});

export const qualitySectionsComplianceSchema = z.object({
  checks: z.array(dashboardComplianceCheckSchema),
  /** Calls with an applicable check — the denominator. */
  evaluatedCalls: countSchema,
  truncated: z.boolean(),
});

export const qualitySectionsCriteriaSchema = z.object({
  /** Flag criteria of the organization (live, and archived ones with rows in the window) — the ones listed. */
  flagCriteria: countSchema,
  /**
   * Score and choice criteria: «triggered of evaluated» has no meaning for
   * them, they are not listed here (their report is the criteria tab) — counted
   * so an empty list is not read as «no criteria».
   */
  otherCriteria: countSchema,
  /**
   * The organization has criteria at all (live or archived), whether or not the
   * window lists any: the card tells «none set up» from «set up, none in the
   * window». Optional so a page deployed ahead of the route (an edge-api
   * rollback) still reads the answer — absent, an empty list reads as «none set up».
   */
  hasCriteria: z.boolean().optional(),
  criteria: z.array(
    z.object({
      criterionId: z.string(),
      name: z.string(),
      /** Calls the criterion was evaluated on. */
      evaluated: countSchema,
      /** Of them, it triggered. */
      triggered: countSchema,
      /** At most this many calls it applied to went unevaluated; `null` — not estimable (paused, archived). */
      notEvaluated: countSchema.nullable(),
    })
  ),
  /**
   * Every criterion of the summary (E4, #1342) — flag, score and choice — as
   * the criteria report lists it, the same reader: the five states
   * (`counts`), the value by type, the version marks, plus the flag's
   * polarity. `flagCriteria`, `otherCriteria` and `criteria` above stay for a
   * UI one release behind (the answer is parsed whole) and go with the old
   * dashboards in E5. Optional so a page deployed ahead of the route (an
   * edge-api rollback) still reads the other five sections: without it the
   * criteria card alone says it could not load.
   */
  rows: z
    .array(
      criteriaReportCriterionSchema.extend({
        /** What a flag looks for — «triggered» means it was found, or that it was not. Not a flag — null. */
        looksFor: z.enum(['presence', 'absence']).nullable(),
      })
    )
    .optional(),
});

/** «Требует проверки» — a scored call below this (or flagged by AI) is a candidate: the list's and the count's one threshold. */
export const QUALITY_REVIEW_SCORE_BELOW = 60;

export const qualitySectionsReviewSchema = z.object({
  /** Calls whose current analysis is flagged by AI or scores below `QUALITY_REVIEW_SCORE_BELOW`. */
  candidates: countSchema,
});

export const dashboardQualitySectionsResponseSchema = z.object({
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  includeDemo: z.enum(['yes', 'no']),
  sections: z.object({
    script: section(qualitySectionsScriptSchema),
    objections: section(qualitySectionsObjectionsSchema),
    compliance: section(qualitySectionsComplianceSchema),
    aiAccuracy: section(dashboardAiAccuracySchema),
    criteria: section(qualitySectionsCriteriaSchema),
    review: section(qualitySectionsReviewSchema),
  }),
});

// ============================================================================
// Types
// ============================================================================

export type DashboardQualitySectionsQuery = z.infer<
  typeof dashboardQualitySectionsQuerySchema
>;
export type QualitySectionsScript = z.infer<typeof qualitySectionsScriptSchema>;
export type QualitySectionsObjections = z.infer<typeof qualitySectionsObjectionsSchema>;
export type QualitySectionsCompliance = z.infer<typeof qualitySectionsComplianceSchema>;
export type QualitySectionsCriteria = z.infer<typeof qualitySectionsCriteriaSchema>;
export type QualitySectionsReview = z.infer<typeof qualitySectionsReviewSchema>;
export type DashboardQualitySectionsResponse = z.infer<
  typeof dashboardQualitySectionsResponseSchema
>;
